import React from 'react';
import styles from './ErrorBoundary.module.css';

const DEFAULT_MESSAGE = "Couldn't load this section.";

/**
 * Shallow compare two `resetKeys` arrays.
 * @param {Array} a
 * @param {Array} b
 * @returns {boolean} true when any entry differs.
 */
const keysChanged = (a = [], b = []) =>
  a.length !== b.length || a.some((item, i) => !Object.is(item, b[i]));

/**
 * ErrorBoundary: contains a render-time crash to one section of the page.
 *
 * Without a boundary, one throwing component unmounts the entire React tree
 * and the user sees a blank white page. Wrap each independent widget (and the
 * app's main content area) so a failure shows a small inline fallback while
 * the sidebar, top bar and sibling widgets keep working.
 *
 * Props:
 * - `name`: short label for the section, used in the console log and test id.
 * - `message`: fallback text (defaults to "Couldn't load this section.").
 * - `resetKeys`: when any value changes (e.g. the active tab), a tripped
 *   boundary clears itself so navigating away and back recovers.
 * - `variant`: `'inline'` (default, for cards) or `'page'` (for the main area).
 * - `onReset`: optional callback run when the user clicks "Try again" (e.g. to
 *   re-fetch the data the section renders).
 *
 * "Try again" remounts the children (a fresh React key), so their state and
 * effects start over and re-fetch.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, attempt: 0 };
    this.handleRetry = this.handleRetry.bind(this);
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, info) {
    // Log only the error type/message and component stack. Never props or
    // state, which can carry user data.
    const label = this.props.name || 'section';
    console.error(
      `[ErrorBoundary] "${label}" failed to render: ${error?.name || 'Error'}: ${error?.message || ''}`,
      info?.componentStack || ''
    );
  }

  componentDidUpdate(prevProps) {
    if (this.state.hasError && keysChanged(prevProps.resetKeys, this.props.resetKeys)) {
      this.reset();
    }
  }

  reset() {
    this.setState((prev) => ({ hasError: false, attempt: prev.attempt + 1 }));
  }

  handleRetry() {
    const { onReset } = this.props;
    if (typeof onReset === 'function') {
      try {
        onReset();
      } catch (err) {
        console.error('[ErrorBoundary] onReset failed:', err?.message || err);
      }
    }
    this.reset();
  }

  render() {
    const { children, name, message = DEFAULT_MESSAGE, variant = 'inline' } = this.props;
    const { hasError, attempt } = this.state;

    if (hasError) {
      const className = [styles.fallback, variant === 'page' ? styles.page : '']
        .filter(Boolean)
        .join(' ');
      return (
        <div
          className={className}
          role="alert"
          data-testid={name ? `error-boundary-${name}` : 'error-boundary'}
        >
          <span className={styles.icon} aria-hidden="true">!</span>
          <p className={styles.message}>{message}</p>
          <button type="button" className={styles.retry} onClick={this.handleRetry}>
            Try again
          </button>
        </div>
      );
    }

    // Keying on `attempt` forces a full remount of the subtree on retry.
    return <React.Fragment key={attempt}>{children}</React.Fragment>;
  }
}
